import {
  listDocName,
  type DocAudience,
  type ListItem,
  type ListMeta,
  type ListType,
} from '@awwyeah/shared';
import { ListChecks, MoreVertical, Plus, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useYArray, useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import {
  AudienceBadge,
  AudiencePicker,
  saveDocAudience,
  useDocAudience,
  type AudienceValue,
} from '../sharing';
import { EmptyState } from './components/EmptyState';
import { MemberAvatar } from './components/MemberAvatar';
import { Sheet } from './components/Sheet';
import { createListMeta } from './listOps';
import { useHouseholdRoster } from './useHouseholdRoster';
import { useListRegistries, type RegisteredList } from './useListRegistries';

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * One row's live summary — opens its own `list:<id>` doc for the count/preview line.
 *
 * The whole card is one tap target (usability testing 2026-08-01: a card with a separate
 * "Start shopping" button *and* a separately-tappable name was two doors into two
 * different screens, and both testers hesitated). "Start shopping" now lives only at the
 * top of the list itself. The open target is a stretched, absolutely-positioned button so
 * the card stays a single real control with the options button layered above it, rather
 * than illegally nesting buttons.
 */
function ListSummary({
  list,
  audience,
  memberCount,
  onMenu,
}: {
  list: ListMeta;
  /** Only for the 🔒 / 👥 badge; `family` renders nothing. */
  audience: DocAudience;
  memberCount: number;
  onMenu: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const { getMember, meId } = useHouseholdRoster();
  const doc = useYDoc(listDocName(list.id));
  const { array } = useYArray<string>(doc, 'order');
  const { entries } = useYMap<ListItem>(doc, 'items');
  const itemMap = new Map(entries);
  const items = array
    .toArray()
    .map((id) => itemMap.get(id))
    .filter((i): i is ListItem => Boolean(i) && !i?.header);
  const done = items.filter((i) => i.checked).length;
  const total = items.length;
  const lastAdded = [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  const assigneeIds = [...new Set(items.flatMap((i) => i.assigneeIds))].slice(0, 3);

  const menuButton = (
    <button
      type="button"
      aria-label={`${list.name} options`}
      data-testid="list-menu-button"
      onClick={(e) => {
        e.stopPropagation();
        onMenu();
      }}
      className="relative z-10 flex h-11 w-8 flex-shrink-0 items-center justify-center text-fg-3"
    >
      <MoreVertical size={18} />
    </button>
  );

  /** Covers the whole card; every other child either sits above it or ignores pointers. */
  const openButton = (
    <button
      type="button"
      aria-label={`Open ${list.name}`}
      data-testid="open-list-button"
      onClick={() => navigate(`/lists/${list.id}`)}
      className="absolute inset-0 z-0 rounded-md"
    />
  );

  if (list.type === 'shopping') {
    const recentlyAdded =
      lastAdded &&
      lastAdded.createdBy !== meId &&
      Date.now() - new Date(lastAdded.createdAt).getTime() < 10 * 60_000;
    return (
      <div
        className="relative flex min-h-[72px] items-center gap-3 rounded-md border-2 border-line-strong bg-surface p-4"
        data-testid="list-row"
        data-list-id={list.id}
        data-list-type="shopping"
      >
        {openButton}
        <ShoppingCart
          size={20}
          className="pointer-events-none relative z-10 flex-shrink-0 text-fg"
        />
        <div className="pointer-events-none relative z-10 min-w-0 flex-1">
          <div className="flex items-center gap-2 text-lg font-bold text-fg">
            <span className="truncate">
              {list.emoji ? `${list.emoji} ` : ''}
              {list.name}
            </span>
            <AudienceBadge audience={audience} memberCount={memberCount} />
          </div>
          {recentlyAdded && lastAdded ? (
            <div className="truncate text-sm font-semibold text-accent">
              {getMember(lastAdded.createdBy).name} added {lastAdded.text} ·{' '}
              {relativeTime(lastAdded.createdAt)}
            </div>
          ) : (
            <div className="text-sm text-fg-3">
              {total === 0 ? 'Nothing on it yet' : `${total - done} to go`}
            </div>
          )}
        </div>
        <span className="pointer-events-none relative z-10 text-base font-bold text-fg-3">
          {total}
        </span>
        {menuButton}
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-[64px] items-center gap-1 border-b border-line last:border-b-0"
      data-testid="list-row"
      data-list-id={list.id}
      data-list-type="todo"
    >
      {openButton}
      <div className="pointer-events-none relative z-10 flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left">
        <ListChecks size={20} className="flex-shrink-0 text-fg-2" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-base font-semibold text-fg">
            <span className="truncate">{list.name}</span>
            <AudienceBadge audience={audience} memberCount={memberCount} />
          </div>
          <div className="text-sm text-fg-3">
            {total === 0 ? 'Empty' : `${done} of ${total} done`}
          </div>
        </div>
        <div className="flex">
          {assigneeIds.map((id, i) => (
            <MemberAvatar
              key={id}
              member={getMember(id)}
              size="sm"
              className={i > 0 ? '-ml-2 border-2 border-surface' : 'border-2 border-surface'}
            />
          ))}
        </div>
      </div>
      {menuButton}
    </div>
  );
}

const EMOJI_CHOICES = ['🛒', '📋', '🧳', '🎉', '🏕️', '🏠', '🎁', '✅'];

function NewListSheet({
  open,
  onClose,
  userId,
  registries,
  members,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  registries: ReturnType<typeof useListRegistries>;
  members: readonly { id: string; name: string; color: string; avatarEmoji?: string }[];
  /** Fires after the meta is written, so the page can PUT the ACL for the new list. */
  onCreated: (meta: ListMeta, audience: AudienceValue) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [audience, setAudience] = useState<AudienceValue>({ audience: 'family', memberIds: [] });
  const [name, setName] = useState('');
  const [type, setType] = useState<ListType>('shopping');
  const [emoji, setEmoji] = useState(EMOJI_CHOICES[0]);
  const [emojiTouched, setEmojiTouched] = useState(false);
  const [store, setStore] = useState('');

  // Suggest a sensible default emoji per type until the household picks one themselves.
  const setListType = (nextType: ListType): void => {
    setType(nextType);
    if (!emojiTouched) setEmoji(nextType === 'shopping' ? '🛒' : '📋');
  };

  const create = (): void => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const meta = createListMeta({
      name: trimmed,
      type,
      userId,
      emoji,
      store: type === 'shopping' && store.trim() ? store.trim() : undefined,
    });
    // The registry the meta lands in *is* the privacy decision for the title, so it is
    // made here at creation rather than by a follow-up move (which would leave the name
    // briefly readable by the whole household).
    const target =
      audience.audience === 'family'
        ? registries.familyMap
        : (registries.personalMap ?? registries.familyMap);
    target.set(meta.id, meta);
    // Mirror into the content doc so anyone this is shared with can read the name.
    registries.mirror(meta);
    onCreated(meta, audience);
    setName('');
    setStore('');
    setAudience({ audience: 'family', memberIds: [] });
    onClose();
    navigate(`/lists/${meta.id}`);
  };

  return (
    <Sheet open={open} onClose={onClose} title="New list" tall testId="new-list-sheet">
      <div className="flex flex-col gap-4 pb-2">
        <div className="flex flex-col gap-1.5">
          <label
            className="text-xs font-semibold uppercase tracking-wide text-fg-3"
            htmlFor="new-list-name"
          >
            Name
          </label>
          <input
            id="new-list-name"
            data-testid="new-list-name-input"
            className="h-12 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
            placeholder="Groceries"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            autoFocus
          />
        </div>
        <div className="flex gap-2">
          {(['shopping', 'todo'] as const).map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`new-list-type-${t}`}
              onClick={() => setListType(t)}
              className={`h-11 flex-1 rounded-full text-sm font-semibold ${
                type === t ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
              }`}
            >
              {t === 'shopping' ? 'Shopping' : 'To-do'}
            </button>
          ))}
        </div>
        {type === 'shopping' && (
          <input
            data-testid="new-list-store-input"
            className="h-12 rounded border border-line bg-bg px-3 text-base text-fg"
            placeholder="Store (optional) — e.g. Costco"
            value={store}
            onChange={(e) => setStore(e.target.value)}
          />
        )}
        <div className="flex flex-wrap gap-2">
          {EMOJI_CHOICES.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                setEmoji(e);
                setEmojiTouched(true);
              }}
              className={`flex h-11 w-11 items-center justify-center rounded-full text-lg ${
                emoji === e ? 'border-2 border-line-strong bg-raised' : 'border border-line'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
            Who can see this
          </span>
          <AudiencePicker
            value={audience}
            onChange={setAudience}
            members={members}
            testId="new-list-audience"
          />
        </div>
        <button
          type="button"
          data-testid="new-list-create"
          disabled={!name.trim()}
          onClick={create}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
        >
          Create list
        </button>
      </div>
    </Sheet>
  );
}

/**
 * "Who can see this" — the picker plus the two writes that have to happen together: the
 * ACL row (what the server enforces) and the registry move (what stops the title leaking).
 * The ACL goes first: if the move somehow fails, the doc is still protected.
 */
function AudienceSheet({
  list,
  members,
  onClose,
  onApplied,
}: {
  list: ListMeta;
  members: readonly { id: string; name: string; color: string; avatarEmoji?: string }[];
  onClose: () => void;
  onApplied: (list: ListMeta, value: AudienceValue) => void;
}): JSX.Element {
  const docName = listDocName(list.id);
  const state = useDocAudience(docName);
  const [draft, setDraft] = useState<AudienceValue | null>(null);
  const [saving, setSaving] = useState(false);
  const value: AudienceValue = draft ?? { audience: state.audience, memberIds: state.memberIds };

  const apply = async (): Promise<void> => {
    setSaving(true);
    const ok = await state.save(value);
    setSaving(false);
    if (!ok) return;
    onApplied(list, value);
    onClose();
  };

  return (
    <Sheet open onClose={onClose} title="Who can see this" tall testId="list-audience-sheet">
      <div className="flex flex-col gap-4 pb-2">
        <p className="text-sm text-fg-2">
          &ldquo;{list.name}&rdquo; — private lists don&apos;t show up for anyone else, not even the
          name.
        </p>
        {state.loading ? (
          <p className="text-sm text-fg-3">Checking…</p>
        ) : (
          <AudiencePicker
            value={value}
            onChange={setDraft}
            members={members}
            disabled={!state.canManage}
            testId="list-audience-picker"
          />
        )}
        {state.error && (
          <p className="text-sm text-danger" data-testid="list-audience-error">
            Couldn&apos;t change that ({state.error}). Nothing was altered.
          </p>
        )}
        {state.canManage && (
          <button
            type="button"
            data-testid="save-list-audience"
            disabled={saving || state.loading}
            onClick={() => void apply()}
            className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </Sheet>
  );
}

function ListActionsSheet({
  list,
  members,
  onClose,
  onArchive,
  onRename,
  onDelete,
  onAudienceApplied,
}: {
  list: ListMeta | null;
  members: readonly { id: string; name: string; color: string; avatarEmoji?: string }[];
  onClose: () => void;
  onArchive: (list: ListMeta) => void;
  onRename: (list: ListMeta, name: string) => void;
  onDelete: (list: ListMeta) => void;
  onAudienceApplied: (list: ListMeta, value: AudienceValue) => void;
}): JSX.Element {
  const [renaming, setRenaming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [draftName, setDraftName] = useState(list?.name ?? '');

  const close = (): void => {
    setRenaming(false);
    setConfirmingDelete(false);
    setSharing(false);
    onClose();
  };

  if (!list) return <></>;

  if (sharing) {
    return (
      <AudienceSheet list={list} members={members} onClose={close} onApplied={onAudienceApplied} />
    );
  }

  if (confirmingDelete) {
    return (
      <Sheet open onClose={close} title={`Delete "${list.name}"?`} testId="delete-list-sheet">
        <div className="flex flex-col gap-3 pb-2">
          <p className="text-sm text-fg-2">
            Everyone loses their copy. You can undo for a few seconds right after.
          </p>
          <button
            type="button"
            data-testid="confirm-delete-list"
            className="h-12 rounded-md bg-danger text-base font-semibold text-surface"
            onClick={() => {
              onDelete(list);
              close();
            }}
          >
            Delete the list
          </button>
          <button
            type="button"
            className="h-12 rounded-md border-2 border-line-strong text-base font-semibold text-fg"
            onClick={() => setConfirmingDelete(false)}
          >
            Keep it
          </button>
        </div>
      </Sheet>
    );
  }

  if (renaming) {
    return (
      <Sheet open onClose={close} title="Rename list" testId="rename-list-sheet">
        <div className="flex flex-col gap-3 pb-2">
          <input
            data-testid="rename-list-input"
            className="h-12 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
            value={draftName}
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draftName.trim()) {
                onRename(list, draftName.trim());
                close();
              }
            }}
          />
          <button
            type="button"
            data-testid="save-rename-list"
            disabled={!draftName.trim()}
            className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
            onClick={() => {
              onRename(list, draftName.trim());
              close();
            }}
          >
            Save
          </button>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet open onClose={close} title={list.name} testId="list-actions-sheet">
      <div className="flex flex-col">
        <button
          type="button"
          data-testid="rename-list-action"
          className="flex h-14 items-center text-left text-base font-medium text-fg"
          onClick={() => {
            setDraftName(list.name);
            setRenaming(true);
          }}
        >
          Rename
        </button>
        <button
          type="button"
          data-testid="share-list-action"
          className="flex h-14 items-center border-t border-line text-left text-base font-medium text-fg"
          onClick={() => setSharing(true)}
        >
          Who can see this
        </button>
        <button
          type="button"
          data-testid="archive-list-action"
          className="flex h-14 items-center border-t border-line text-left text-base font-medium text-fg"
          onClick={() => {
            onArchive(list);
            close();
          }}
        >
          {list.archived ? 'Unarchive' : 'Archive'}
        </button>
        <button
          type="button"
          data-testid="delete-list-action"
          className="flex h-14 items-center border-t border-line text-left text-base font-medium text-danger"
          onClick={() => setConfirmingDelete(true)}
        >
          Delete
        </button>
      </div>
    </Sheet>
  );
}

export function ListsIndexPage(): JSX.Element {
  const { user } = useUser();
  const navigate = useNavigate();
  const registries = useListRegistries();
  const roster = useHouseholdRoster();
  const [newListOpen, setNewListOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [menuListId, setMenuListId] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ meta: ListMeta; personal: boolean } | null>(null);
  /** Audience per list id, kept locally so a flip badges the row immediately. */
  const [audiences, setAudiences] = useState<Record<string, AudienceValue>>({});

  /** Everyone but me — I am always in my own circle, so offering myself is noise. */
  const otherMembers = [...roster.members.values()]
    .filter((member) => member.id !== user?.id)
    .map((member) => ({
      id: member.id,
      name: member.name,
      color: member.color,
      avatarEmoji: member.avatarEmoji,
    }));

  const entries: RegisteredList[] = registries.lists;
  const lists = registries.metas;
  const active = entries.filter((e) => !e.meta.archived);
  const archived = entries
    .filter((e) => e.meta.archived)
    .sort((a, b) => b.meta.updatedAt.localeCompare(a.meta.updatedAt));
  const shopping = active.filter((e) => e.meta.type === 'shopping');
  const todo = active.filter((e) => e.meta.type === 'todo');
  const menuList = lists.find((l) => l.id === menuListId) ?? null;

  /**
   * A row's badge. The exact audience is only known after the picker has been opened (or
   * flipped) this session; until then the registry a list lives in is the honest signal —
   * anything outside the shared registry is not family-visible.
   */
  const audienceFor = (entry: RegisteredList): AudienceValue => {
    const known = audiences[entry.meta.id];
    if (known) return known;
    if (entry.sharedWithMe) return { audience: 'members', memberIds: [] };
    return { audience: entry.personal ? 'private' : 'family', memberIds: [] };
  };

  /**
   * The registry map a given list's meta lives in — writes must go to the right one.
   * `null` for a list somebody else shared with me: I have no registry entry for it, and
   * writing one into the shared `lists` doc would republish its title to the household.
   */
  const mapFor = (listId: string): typeof registries.familyMap | null => {
    const where = registries.registryOf(listId);
    if (where === 'shared-with-me') return null;
    if (where === 'personal' && registries.personalMap) return registries.personalMap;
    return registries.familyMap;
  };

  const touch = (list: ListMeta, patch: Partial<ListMeta>): void => {
    if (!user) return;
    const next: ListMeta = {
      ...list,
      ...patch,
      updatedBy: user.id,
      updatedAt: new Date().toISOString(),
    };
    const target = mapFor(list.id);
    // A list shared *with* me has no registry entry of mine to write; the mirror inside
    // the content doc is the only copy I can reach, and it is the one others read.
    if (target) target.set(list.id, next);
    registries.mirror(next);
  };

  const reuse = (list: ListMeta): void => {
    touch(list, { archived: false });
    navigate(`/lists/${list.id}`);
  };

  const deleteList = (list: ListMeta): void => {
    const personal = registries.registryOf(list.id) === 'personal';
    mapFor(list.id)?.delete(list.id);
    setUndo({ meta: list, personal });
    setTimeout(() => setUndo((current) => (current?.meta.id === list.id ? null : current)), 8000);
  };

  const undoDelete = (): void => {
    if (undo) {
      const target =
        undo.personal && registries.personalMap ? registries.personalMap : registries.familyMap;
      target.set(undo.meta.id, undo.meta);
    }
    setUndo(null);
  };

  /** ACL is already written by the sheet; this is the registry half plus the local badge. */
  const applyAudience = (list: ListMeta, value: AudienceValue): void => {
    registries.placeInRegistry(list, value.audience);
    setAudiences((current) => ({ ...current, [list.id]: value }));
  };

  if (!user) return <></>;

  return (
    <div className="flex flex-col gap-6 py-4" data-testid="lists-index-page">
      <header className="flex items-center justify-between">
        <h2 className="font-display text-2xl tracking-tight text-fg">Lists</h2>
        <button
          type="button"
          data-testid="new-list-button-header"
          aria-label="New list"
          className="flex h-11 items-center gap-1.5 rounded-md border-2 border-line-strong px-3 text-sm font-semibold text-fg"
          onClick={() => setNewListOpen(true)}
        >
          <Plus size={16} /> New list
        </button>
      </header>

      {/* Registries load from IndexedDB asynchronously (offline-first); an empty `lists`
          before that finishes isn't "no lists", it's "hasn't hydrated yet" — most visible
          right after an update-triggered reload. Render nothing until `locallyLoaded`,
          rather than flashing the empty state over a household that has plenty
          (WS-update-reload audit, mirrors AppsIndexPage). */}
      {!registries.locallyLoaded ? null : lists.length === 0 ? (
        <EmptyState
          title="No lists yet"
          body="Groceries is probably the one. It'll be on everyone's phone the second you make it."
          actionLabel="Make a grocery list"
          onAction={() => setNewListOpen(true)}
          secondary={
            <span className="text-xs text-fg-3">
              or{' '}
              <button
                type="button"
                className="font-semibold text-member-faye"
                onClick={() => navigate('/lists/templates')}
              >
                start from a template
              </button>
            </span>
          }
        />
      ) : (
        <>
          {shopping.length > 0 && (
            <section className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
                Shopping
              </span>
              <div className="flex flex-col gap-3">
                {shopping.map((entry) => (
                  <ListSummary
                    key={entry.meta.id}
                    list={entry.meta}
                    audience={audienceFor(entry).audience}
                    memberCount={audienceFor(entry).memberIds.length}
                    onMenu={() => setMenuListId(entry.meta.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {todo.length > 0 && (
            <section className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">To-do</span>
              <div className="flex flex-col overflow-hidden rounded-md border border-line bg-surface">
                {todo.map((entry) => (
                  <ListSummary
                    key={entry.meta.id}
                    list={entry.meta}
                    audience={audienceFor(entry).audience}
                    memberCount={audienceFor(entry).memberIds.length}
                    onMenu={() => setMenuListId(entry.meta.id)}
                  />
                ))}
              </div>
            </section>
          )}

          <button
            type="button"
            data-testid="archived-toggle"
            className="flex min-h-[48px] items-center justify-between border-t border-line pt-2 text-left"
            onClick={() => setShowArchived((v) => !v)}
          >
            <span className="text-sm text-fg-2">Archived</span>
            <span className="text-sm text-fg-3">
              {archived.length} {showArchived ? '↓' : '→'}
            </span>
          </button>
          {showArchived && (
            <div className="flex flex-col gap-2" data-testid="archived-list">
              {archived.length === 0 ? (
                <p className="text-sm text-fg-3">
                  Nothing archived yet. Finished lists land here instead of disappearing.
                </p>
              ) : (
                archived.map((entry) => (
                  <div
                    key={entry.meta.id}
                    className="flex items-center gap-3 rounded-md border border-line bg-surface p-3"
                    data-testid="archived-row"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm text-fg-2">
                        <span className="truncate">
                          {entry.meta.emoji ? `${entry.meta.emoji} ` : ''}
                          {entry.meta.name}
                        </span>
                        <AudienceBadge
                          audience={audienceFor(entry).audience}
                          memberCount={audienceFor(entry).memberIds.length}
                        />
                      </div>
                      <div className="text-xs text-fg-3">
                        Archived {relativeTime(entry.meta.updatedAt)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-sm font-semibold text-member-faye"
                      onClick={() => reuse(entry.meta)}
                      data-testid="reuse-list"
                    >
                      Reuse
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      <div className="mt-auto flex items-center gap-3 pt-2">
        <button
          type="button"
          data-testid="new-list-button"
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-2 border-line-strong text-sm font-semibold text-fg"
          onClick={() => setNewListOpen(true)}
        >
          <Plus size={16} /> New list
        </button>
        <button
          type="button"
          data-testid="templates-link"
          className="flex-shrink-0 text-sm font-semibold text-member-faye"
          onClick={() => navigate('/lists/templates')}
        >
          Templates →
        </button>
      </div>

      {undo && (
        <div
          className="fixed inset-x-4 bottom-[calc(88px+env(safe-area-inset-bottom))] z-50 flex items-center gap-3 rounded-md bg-inverse px-4 py-3 text-inverse-fg"
          data-testid="undo-toast"
        >
          <span className="flex-1 text-sm">Deleted "{undo.meta.name}"</span>
          <button
            type="button"
            className="text-sm font-bold text-accent"
            onClick={undoDelete}
            data-testid="undo-delete-list"
          >
            Undo
          </button>
        </div>
      )}

      <NewListSheet
        open={newListOpen}
        onClose={() => setNewListOpen(false)}
        userId={user.id}
        registries={registries}
        members={otherMembers}
        onCreated={(meta, value) => {
          setAudiences((current) => ({ ...current, [meta.id]: value }));
          // Fire-and-forget: the meta is already in the private registry, so the title is
          // not exposed while this lands. A failure leaves the *content* doc family-wide,
          // which the next open of "Who can see this" shows honestly rather than hiding.
          if (value.audience !== 'family') {
            void saveDocAudience(listDocName(meta.id), {
              audience: value.audience,
              memberIds: value.memberIds,
            }).catch(() => undefined);
          }
        }}
      />
      <ListActionsSheet
        list={menuList}
        members={otherMembers}
        onClose={() => setMenuListId(null)}
        onArchive={(list) => touch(list, { archived: !list.archived })}
        onRename={(list, name) => touch(list, { name })}
        onDelete={deleteList}
        onAudienceApplied={applyAudience}
      />
    </div>
  );
}
